/**
 * AlertForm Component
 * 
 * A multi-step form for creating and editing alerts in the DeepRDashboard alert system.
 * This component provides a wizard-like interface with the following steps:
 * 1. Alert Type Selection - Choose the type of alert (Global, SystemEvent, Process, Log)
 * 2. Event Selection - Select specific events to monitor based on the alert type
 * 3. Rule Configuration - Set conditions and values for the alert rules
 * 4. Server Scope - Define whether the alert applies globally or to specific servers
 * 5. Alert Details - Set name, description, and severity
 * 6. Action Selection - Choose notification methods
 * 
 * The component integrates with AlertContext for state management and form submission.
 */

import React, { useState, useEffect } from 'react';
import { useAlertContext } from '../../contexts/AlertContext';

// Import CSS for the AlertForm and progress indicator
import '../../styles/themes/dark/AlertForm/AlertForm.css';
import '../../styles/themes/light/AlertForm/AlertForm.css';
import '../../styles/themes/dark/AlertForm/ProgressIndicator.css';
import '../../styles/themes/light/AlertForm/ProgressIndicator.css';
// Import directly using CommonJS-compatible syntax
import { 
  AlertType, 
  AlertSeverity, 
  AlertAction, 
  getAlertTypeDisplayText,
  getAlertTypeIcon,
  getEventsForAlertType,
  createAlertRule
} from '../../models/AlertModels';

// Import all step components from the index file
import {
  TypeSelection,
  EventSelection,
  RuleBuilder,
  ServerScope,
  AlertDetails,
  ActionSelection,
  ReviewSubmit
} from './steps';

/**
 * AlertForm component for creating and editing alerts
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.hideTitle - Whether to hide the title (useful when displayed in a Modal)
 * @returns {JSX.Element} The AlertForm component
 */
function AlertForm({ hideTitle = false }) {
  const { 
    formState, 
    updateFormState, 
    resetFormState, 
    createAlert, 
    updateAlert 
  } = useAlertContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Define the steps of the form
  const steps = [
    { id: 'type', title: 'Alert Type', component: TypeSelection },
    { id: 'event', title: 'Event Selection', component: EventSelection },
    { id: 'rules', title: 'Rule Configuration', component: RuleBuilder },
    { id: 'scope', title: 'Server Scope', component: ServerScope },
    { id: 'details', title: 'Alert Details', component: AlertDetails },
    { id: 'actions', title: 'Action Selection', component: ActionSelection },
    { id: 'review', title: 'Review & Submit', component: ReviewSubmit }
  ];

  // Get the current step component
  const CurrentStepComponent = steps[formState.currentStep]?.component;

  /**
   * Handle next step button click
   */
  const handleNext = () => {
    if (formState.currentStep < steps.length - 1) {
      updateFormState({ currentStep: formState.currentStep + 1 });
    }
  };

  /**
   * Handle previous step button click
   */
  const handlePrevious = () => {
    if (formState.currentStep > 0) {
      updateFormState({ currentStep: formState.currentStep - 1 });
    }
  };

  /**
   * Handle form cancellation
   */
  const handleCancel = () => {
    if (window.confirm('Are you sure you want to cancel? All changes will be lost.')) {
      resetFormState();
    }
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setSubmitError(null);
      
      const alertData = formState.alert;
      
      let result;
      if (formState.isEditing) {
        result = await updateAlert(alertData.id, alertData);
        setSuccessMessage('Alert updated successfully!');
      } else {
        result = await createAlert(alertData);
        setSuccessMessage('Alert created successfully!');
      }
      
      // Reset form after successful submission
      setTimeout(() => {
        resetFormState();
        setSuccessMessage(null);
      }, 2000);
      
    } catch (error) {
      console.error('Error submitting alert:', error);
      setSubmitError(error.message || 'An error occurred while saving the alert');
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Check if the current step is valid and the Next button should be enabled
   * 
   * @returns {boolean} Whether the current step is valid
   */
  const isCurrentStepValid = () => {
    // For EventSelection step, require at least one event to be selected
    if (formState.currentStep === 1) { // EventSelection is step 1
      return formState.alert.rules && formState.alert.rules.length > 0;
    }
    
    // For RuleBuilder step, require all rules to have valid values
    if (formState.currentStep === 2) { // RuleBuilder is step 2
      if (!formState.alert.rules || formState.alert.rules.length === 0) {
        return false;
      }
      
      // Check if all rules have values
      return formState.alert.rules.every(rule => {
        // For connection-related events, require explicit true/false selection
        if (['Websocket.Connected', 'Websocket.Disconnected', 'Server.Connected', 'Server.Disconnected'].includes(rule.event)) {
          return rule.value === 'true' || rule.value === 'false';
        }
        // For other events, just check that value is not empty
        return rule.value !== undefined && rule.value !== null && rule.value !== '';
      });
    }
    
    // All other steps are always valid
    return true;
  };

  // Render progress indicator with progress tracker
  const renderProgress = () => {
    // Calculate width for the progress connector based on current step
    const stepCount = steps.length - 1; // Adjust for zero-based index
    const progressPercentage = (formState.currentStep / stepCount) * 100;
    const connectorWidth = progressPercentage > 0 ? `calc(${progressPercentage}% - 28px)` : '0';
    
    return (
      <div className="alert-form-progress">
        {/* Progress connector - the colored line showing progress */}
        <div 
          className="progress-connector" 
          style={{ width: connectorWidth }}
        />
        
        {/* Steps */}
        {steps.map((step, index) => (
          <div 
            key={step.id}
            className={`progress-step ${index === formState.currentStep ? 'active' : ''} 
                       ${index < formState.currentStep ? 'completed' : ''}`}
            onClick={() => {
              // Allow navigation to completed steps and current step
              if (index <= formState.currentStep) {
                updateFormState({ currentStep: index });
              }
            }}
            style={{ cursor: index <= formState.currentStep ? 'pointer' : 'default' }}
          >
            <div className="step-indicator">
              {/* Show step number for future steps and current step */}
              {index > formState.currentStep ? index + 1 : 
                /* For completed steps, the checkmark will be shown via CSS */
                index < formState.currentStep ? '' : index + 1
              }
            </div>
            <div className="step-title">{step.title}</div>
          </div>
        ))}
      </div>
    );
  };

  // Render navigation buttons
  const renderNavigation = () => {
    const isFirstStep = formState.currentStep === 0;
    const isLastStep = formState.currentStep === steps.length - 1;
    const isStepValid = isCurrentStepValid();
    
    return (
      <div className="alert-form-navigation">
        <button 
          className="btn btn-secondary" 
          onClick={handleCancel}
        >
          Cancel
        </button>
        
        <div className="navigation-buttons">
          {!isFirstStep && (
            <button 
              className="btn btn-outline" 
              onClick={handlePrevious}
              disabled={isSubmitting}
            >
              Previous
            </button>
          )}
          
          {!isLastStep ? (
            <button 
              className="btn btn-primary" 
              onClick={handleNext}
              disabled={!isStepValid}
              title={!isStepValid ? 'Please complete this step before proceeding' : ''}
            >
              Next
            </button>
          ) : (
            <button 
              className="btn btn-success" 
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : formState.isEditing ? 'Update Alert' : 'Create Alert'}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="alert-form-container">
      {!hideTitle && (
        <h2 className="alert-form-title">
          {formState.isEditing ? 'Edit Alert' : 'Create New Alert'}
          <div className="alert-form-subtitle">
            Step {formState.currentStep + 1} of {steps.length}: {steps[formState.currentStep]?.title}
          </div>
        </h2>
      )}
      
      {renderProgress()}
      
      {successMessage && (
        <div className="alert-success-message">
          {successMessage}
        </div>
      )}
      
      {submitError && (
        <div className="alert-error-message">
          {submitError}
        </div>
      )}
      
      <div className="alert-form-content">
        {CurrentStepComponent && <CurrentStepComponent />}
      </div>
      
      {renderNavigation()}
    </div>
  );
}

export default AlertForm;
